import type { RoomSnapshot } from '../../lib/api/rooms'
import { RoomQR } from '../../components/RoomQR'
import { useT } from '../../i18n'

interface Props { snapshot: RoomSnapshot }

/**
 * Lobby phase: giant join URL + room code + QR, live player list. The
 * TV just waits — the host starts the game from their phone.
 * Shown only while room.status === 'lobby'.
 */
export function TVLobbyPhase({ snapshot }: Props) {
  const t = useT()
  const { room, players } = snapshot

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full max-w-7xl items-center">
      {/* Left: room code is the hero, instructions below */}
      <div className="text-center md:text-left">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-3">{t('Room code')}</p>
        <p className="font-display font-bold text-game-iet-dark
                      tracking-[0.15em] leading-none mb-10
                      text-[9rem] md:text-[12rem]">
          {room.code}
        </p>
        <p className="text-2xl text-brand-muted mb-2">{t('On your phone, go to')}</p>
        <p className="font-display text-4xl md:text-5xl font-bold text-brand-text
                      break-all leading-tight mb-3">
          games.jordandraper.com/join
        </p>
        <p className="text-xl text-brand-muted mb-8">
          {t('and enter the code.')} {t('No account needed. Guests can play.')}
        </p>
        <div className="flex justify-center md:justify-start">
          <RoomQR code={room.code} size={200} />
        </div>
      </div>

      {/* Right: player list */}
      <div>
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-muted mb-4
                      text-center md:text-left">
          {players.length === 0
            ? t('Waiting for players…')
            : players.length === 1
              ? t('1 player joined')
              : t('{n} players joined', { n: players.length })}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {players.map(p => (
            <div key={p.id}
                 className="card p-4 text-center text-xl font-semibold
                            animate-scale-in bg-white">
              {p.display_name}
            </div>
          ))}
          {players.length === 0 && (
            <div className="col-span-2 card p-8 text-center text-brand-muted">{t("No one's here yet. Share the code above.")}</div>
          )}
        </div>

        <div className="mt-8 text-center md:text-left">
          <p className="text-2xl text-brand-muted animate-pulse">
            {players.length < 2
              ? t('Waiting for 2+ players…')
              : t('Host starts the game from their phone.')}
          </p>
        </div>
      </div>
    </div>
  )
}
